import type { HttpInterceptorFn } from '@angular/common/http';
import type { EnvironmentProviders, Provider } from '@angular/core';
import { computed, inject, Injector } from '@angular/core';
import { adpScopedInterceptor } from '@app/adp-interceptor.providers';
import { ImpersonationService } from '@core/services/impersonation.service';
import { environment } from '@env/environment';
import { TRANSLOCO_MISSING_HANDLER } from '@jsverse/transloco';
import { NamedRoleMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import type {
  DxGrantEntry,
  DxGrantsContext,
  DxKnownRole,
  DxSessionContext,
} from '@kerberos-compliance/compliance-shared-ui/dx';
import {
  DxI18nService,
  isDxLevelReachableInBuild,
  isDxSuiteReachableInBuild,
  provideDxTooling,
} from '@kerberos-compliance/compliance-shared-ui/dx';
import { AuthStore } from '@/auth/store/auth.store';
import { DxMissingTranslationHandler } from './dx-missing-translation.handler';
import { dxPanels } from './dx-panels';
import { dxKnownStorageKeys, dxStorageRedactionPolicies, dxStorageResetGroups } from './dx-redaction';
import type { DxToolId } from './dx-registry';
import { dxToolRegistry } from './dx-registry';
import type { DxFlowRecord } from './dx-sinks';
import { dxFlowSink, dxMissingTranslationSink } from './dx-sinks';
import {
  dxAuthStatusContributor,
  dxBuildStatusContributor,
  dxFlowStatusContributor,
  dxSessionStatusContributor,
} from './dx-status.contributors';
import { dxTelemetryInterceptor, DxTelemetryService } from './dx-telemetry.service';

/**
 * Roles that make a session internal for gating purposes. The gate itself lives in
 * `compliance-shared-ui` and knows nothing about AML Desk's role model, so the app
 * answers the question and hands the shell a boolean signal.
 */
const dxInternalRoles = new Set<string>([
  NamedRoleMap.SystemAdministrator,
  NamedRoleMap.InternalUser,
  NamedRoleMap.CustomerImpersonator,
]);

/**
 * Tools that need wiring on the app's own request path. Their registration is decided
 * here - before DI exists - rather than gated at render time, so a build that can never
 * render them does not ship their collector either.
 */
const dxWiredTools: DxToolId[] = ['network-interceptor'];

/**
 * Tools whose production exposure has actually been decided, as opposed to merely
 * claimed. A descriptor's `internal-role` or `impersonation` level says what a tool could
 * justify; naming it here is the decision that it may, and the shared gate
 * refuses every production-capable level that is not on this list.
 *
 * These three are exactly the tools the app's own gate reached on production before the
 * suite moved into `compliance-shared-ui`, so the list is a record of the existing
 * decision rather than a new one. It stays inert while the production configuration
 * replaces this module with one that returns `[]`.
 */
const dxProductionAllowlist: DxToolId[] = ['auth-explainer', 'session-inspector', 'diagnostics-bundle'];

/**
 * The roles worth a checklist row in the shared grants panel, including the ones a
 * session does not hold - "which of these am I missing?" is the question it is opened for.
 *
 * The three that make a session internal are the same set as `dxInternalRoles` above, and
 * deliberately so: the panel explains the gate rather than restating it.
 */
const dxKnownRoles: readonly DxKnownRole[] = [
  { role: NamedRoleMap.SystemAdministrator, description: 'Superadmin privileges across organizations' },
  { role: NamedRoleMap.InternalUser, description: 'Kerberos internal platform operator' },
  { role: NamedRoleMap.CustomerImpersonator, description: 'Allows impersonation of customer user accounts' },
  { role: NamedRoleMap.BasicUser, description: 'Standard organization member' },
  { role: NamedRoleMap.WorkItemUser, description: 'Permitted to process KYC & AML work items' },
  { role: NamedRoleMap.BillingUser, description: 'Access to billing and subscriptions' },
];

/**
 * Resolves the session's grants for the shared grants panel.
 *
 * Read through `AuthStore`, never re-derived: `resolveGrant()` encodes precedence rules
 * (platform-level match, `BasicUser` de-prioritization, org scoping) that a second
 * implementation in DX code would drift away from, and a grants panel that disagrees with
 * the app is worse than none. No access token is passed: the panel's token diagnostics
 * section stays absent rather than decoding a JWT this app holds in storage.
 *
 * @returns {DxGrantsContext} The grants context for this session.
 */
function dxGrantsContext(): DxGrantsContext {
  const authStore = inject(AuthStore);

  return {
    isLoaded: authStore.grantLoadingComplete,
    isAuthenticated: authStore.isUserLoggedIn,
    organizationId: computed(() => authStore.organizationId() ?? undefined),
    knownRoles: dxKnownRoles,
    grants: computed<readonly DxGrantEntry[]>(() =>
      (authStore.ownGrants() ?? []).map((grant) => ({
        role: grant.roleDisplayName,
        roleId: grant.roleId,
        tenantId: grant.tenantId,
        organizationId: grant.organizationId,
        isPlatformLevel: grant.organizationId === undefined,
      })),
    ),
  };
}

/**
 * Resolves the session signals the shared gate evaluates its production-capable levels
 * against. Runs inside an injection context: the store and the impersonation service
 * cannot be reached while the provider array is still being assembled.
 *
 * @returns {DxSessionContext} The internal-role and impersonation signals for this session.
 */
function dxSessionContext(): DxSessionContext {
  const authStore = inject(AuthStore);
  const impersonationService = inject(ImpersonationService);

  return {
    hasInternalRole: computed(() => authStore.roleDisplayNames().some((role) => dxInternalRoles.has(role))),
    isImpersonating: computed(() => impersonationService.isActive()),
  };
}

/**
 * Whether a tool could ever be reached in this build, ignoring session state.
 *
 * @param {DxToolId} id - The tool to check.
 * @returns {boolean} True when some session in this build could reach it.
 */
function isToolReachableInBuild(id: DxToolId): boolean {
  // The allowlist is passed here too, so this pre-DI verdict cannot be laxer than the
  // one the gate reaches at render time.
  return isDxLevelReachableInBuild(dxToolRegistry[id].level, environment.target, dxProductionAllowlist.includes(id));
}

/**
 * HTTP interceptors required by DX tooling. Returns an empty list when no wired tool
 * could ever be enabled in this build, so DX code stays off the customer path entirely
 * rather than being gated at render time.
 *
 * Only the local-only edge-case overrides are left here. Request metadata is no longer
 * recorded at all: the browser's own network tab answers the same question, and it does
 * so without a buffer of URLs and correlation ids living inside the app.
 *
 * Host-scoped like the app's own interceptors: DX never touches Hanko, DMS or
 * third-party traffic.
 *
 * @returns {HttpInterceptorFn[]} Interceptors to append to the app's HTTP pipeline.
 */
export function dxHttpInterceptors(): HttpInterceptorFn[] {
  return dxWiredTools.some((id) => isToolReachableInBuild(id)) ? [adpScopedInterceptor(dxTelemetryInterceptor)] : [];
}

/**
 * Wires the shared DX suite for AML Desk, plus the collection hooks only this app can
 * offer. Each hook is provided only where the tool that consumes it could be reached,
 * so a production customer session wires up nothing at all.
 *
 * @returns {(Provider | EnvironmentProviders)[]} Providers to append to the application provider list.
 */
export function dxProviders(): (Provider | EnvironmentProviders)[] {
  if (!isDxSuiteReachableInBuild(dxToolRegistry, environment.target, dxProductionAllowlist)) {
    return [];
  }

  const providers: (Provider | EnvironmentProviders)[] = [
    ...provideDxTooling({
      registry: dxToolRegistry,
      target: environment.target,
      badge: 'AML Desk',
      panels: dxPanels,
      session: dxSessionContext,
      allowOnProduction: dxProductionAllowlist,
      grants: dxGrantsContext,
      // The library's own buffer, fed by the app's Transloco handler below. The panel and
      // the buffer are both shared now; only the hook into Transloco stays here, because
      // Transloco is not a dependency of `compliance-shared-ui`.
      i18n: () => inject(DxI18nService),
      storage: {
        policies: dxStorageRedactionPolicies,
        knownKeys: dxKnownStorageKeys,
        resetGroups: dxStorageResetGroups,
      },
      statusContributors: [
        dxBuildStatusContributor,
        dxAuthStatusContributor,
        dxSessionStatusContributor,
        dxFlowStatusContributor,
      ],
    }),
  ];

  // The sinks resolve the telemetry service lazily, on first use. Resolving it in the
  // factory would pull the gate - and the auth store - into the construction of the
  // app's translation service, closing a provider cycle at bootstrap.
  if (isToolReachableInBuild('hanko-flow-timeline')) {
    providers.push({
      provide: dxFlowSink,
      useFactory: (): ((record: Omit<DxFlowRecord, 'id'>) => void) => {
        const injector = inject(Injector);
        return (record) => injector.get(DxTelemetryService).logFlowTransition(record);
      },
    });
  }

  if (isToolReachableInBuild('a11y-i18n-inspector')) {
    providers.push(
      {
        provide: dxMissingTranslationSink,
        useFactory: (): ((key: string) => void) => {
          const injector = inject(Injector);
          return (key) => injector.get(DxI18nService).logMissingTranslation(key);
        },
      },
      { provide: TRANSLOCO_MISSING_HANDLER, useClass: DxMissingTranslationHandler },
    );
  }

  return providers;
}
